import React, { useState, useRef } from 'react';
import { Form, Row, Col, Input, Button } from 'reactstrap';
import Select from 'react-select';
import { ModalButtonBuilder, ToastBuilder, ApiRequest } from '../../../../../../../../../GlobalComponents/Builders';

/**
 * ScriptBuilder — Structured builder for after_create_scripts.
 *
 * Fetches script schemas from the dmt_script table and renders dynamic forms
 * for configuring registered scripts. Unregistered scripts are editable as raw text.
 *
 * Output format:
 *   Registered: sync_comments[{input:{key:val|key2:val2}|settings:{key:val|key2:val2}}]
 *   Unregistered: raw string as-is
 *   Multiple: comma-separated
 */

const selectStyles = {
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    control: (base) => ({ ...base, minHeight: 30, height: 30, fontSize: 12 }),
    valueContainer: (base) => ({ ...base, padding: '0 6px' }),
    indicators: (base) => ({ ...base, height: 28 }),
    indicator: (base) => ({ ...base, padding: 4 }),
    singleValue: (base) => ({ ...base, fontSize: 12 }),
    placeholder: (base) => ({ ...base, fontSize: 12 }),
    input: (base) => ({ ...base, margin: 0, padding: 0 }),
};

// ─── Parsing Utilities ──────────────────────────────────────────────────────

/**
 * Split a script string on commas, but NOT commas inside brackets.
 */
function splitScripts(value) {
    if (!value || !value.trim()) return [];
    const parts = [];
    let current = '';
    let depth = 0;
    for (const char of value) {
        if (char === '[') depth++;
        else if (char === ']') depth--;
        if (char === ',' && depth === 0) {
            parts.push(current.trim());
            current = '';
        } else {
            current += char;
        }
    }
    if (current.trim()) parts.push(current.trim());
    return parts;
}

/**
 * Parse a single script entry into { name, input, settings, raw }.
 */
function parseScriptEntry(entry) {
    const nameMatch = entry.match(/^([a-zA-Z0-9_]+)/);
    if (!nameMatch) return { name: '', input: {}, settings: {}, raw: entry };

    const name = nameMatch[1];
    const bracketContent = entry.slice(name.length);

    if (!bracketContent || !bracketContent.startsWith('[')) {
        return { name, input: {}, settings: {}, raw: null };
    }

    // Extract content between outer [ ]
    const inner = bracketContent.slice(1, -1); // remove [ and ]
    if (!inner.startsWith('{') || !inner.endsWith('}')) {
        return { name, input: {}, settings: {}, raw: null };
    }

    const content = inner.slice(1, -1); // remove { and }

    // Split on top-level | to find input: and settings: sections
    const sections = splitTopLevel(content, '|');
    let input = {};
    let settings = {};

    for (const section of sections) {
        if (section.startsWith('input:')) {
            const inputRaw = section.slice(6); // remove "input:"
            input = parseKVSection(inputRaw);
        } else if (section.startsWith('settings:')) {
            const settingsRaw = section.slice(9); // remove "settings:"
            // Legacy format: settings:(flag1|flag2|flag3) — array of flags
            if (settingsRaw.startsWith('(') && settingsRaw.endsWith(')')) {
                const flags = settingsRaw.slice(1, -1).split('|');
                for (const flag of flags) {
                    settings[flag.trim()] = true;
                }
            } else {
                settings = parseKVSection(settingsRaw);
            }
        }
    }

    return { name, input, settings, raw: null };
}

/**
 * Split string on separator at the top level (not inside { } or ( ))
 */
function splitTopLevel(s, sep = '|') {
    const parts = [];
    let current = '';
    let depthParen = 0;
    let depthBrace = 0;

    for (const char of s) {
        if (char === '(') depthParen++;
        else if (char === ')') depthParen--;
        else if (char === '{') depthBrace++;
        else if (char === '}') depthBrace--;

        if (char === sep && depthParen === 0 && depthBrace === 0) {
            parts.push(current.trim());
            current = '';
        } else {
            current += char;
        }
    }
    if (current.trim()) parts.push(current.trim());
    return parts;
}

/**
 * Parse a {key:value|key2:value2} section (without the outer braces).
 */
function parseKVSection(raw) {
    if (!raw) return {};
    // Remove surrounding braces if present
    let content = raw.trim();
    if (content.startsWith('{') && content.endsWith('}')) {
        content = content.slice(1, -1);
    }
    const result = {};
    const parts = splitTopLevel(content, '|');
    for (const part of parts) {
        const colonIdx = part.indexOf(':');
        if (colonIdx === -1) continue;
        const key = part.slice(0, colonIdx).trim();
        let val = part.slice(colonIdx + 1).trim();
        // Parse array format (val1|val2|val3) → comma-separated string for display
        if (val.startsWith('(') && val.endsWith(')')) {
            const inner = val.slice(1, -1);
            val = inner.split('|').map(s => s.trim()).join(', ');
        }
        // Parse booleans and numbers
        else if (val === 'true') val = true;
        else if (val === 'false') val = false;
        else if (val !== '' && !isNaN(val) && !isNaN(Number(val))) val = Number(val);
        // Decode legacy semicolons back to commas for display
        else if (typeof val === 'string') val = val.replace(/;/g, ',');
        result[key] = val;
    }
    return result;
}

// ─── Serialization ──────────────────────────────────────────────────────────

/**
 * Serialize a script object back to the output format.
 */
function serializeScript(script) {
    if (script.raw !== null && script.raw !== undefined) return script.raw;

    const hasInput = Object.keys(script.input || {}).length > 0;
    const hasSettings = Object.keys(script.settings || {}).length > 0;

    if (!hasInput && !hasSettings) return script.name;

    const serializeValue = (v) => {
        if (v === true) return 'true';
        if (v === false) return 'false';
        // Encode comma-separated lists as pipe-delimited arrays in parens: (val1|val2)
        // This uses the existing array format that parse_script_args already supports
        if (typeof v === 'string' && v.includes(',')) {
            const items = v.split(',').map(s => s.trim()).filter(Boolean);
            return `(${items.join('|')})`;
        }
        return v;
    };

    const parts = [];
    if (hasInput) {
        const inputPairs = Object.entries(script.input)
            .filter(([, v]) => v !== '' && v !== null && v !== undefined)
            .map(([k, v]) => `${k}:${serializeValue(v)}`);
        if (inputPairs.length > 0) parts.push(`input:{${inputPairs.join('|')}}`);
    }
    if (hasSettings) {
        const settingsPairs = Object.entries(script.settings)
            .filter(([, v]) => v !== '' && v !== null && v !== undefined)
            .map(([k, v]) => `${k}:${serializeValue(v)}`);
        if (settingsPairs.length > 0) parts.push(`settings:{${settingsPairs.join('|')}}`);
    }

    if (parts.length === 0) return script.name;
    return `${script.name}[{${parts.join('|')}}]`;
}

// ─── Component ──────────────────────────────────────────────────────────────

const ScriptBuilder = ({ value, onChange, readOnly, jobData }) => {

    const [scripts, setScripts] = useState([]);
    const [schemas, setSchemas] = useState([]);
    const [schemasLoaded, setSchemasLoaded] = useState(false);
    const [expandedIndices, setExpandedIndices] = useState(new Set());
    const [advancedOpen, setAdvancedOpen] = useState(new Set());
    const schemasCache = useRef(null);
    const dragItem = useRef(null);
    const dragOverItem = useRef(null);

    const sourceService = jobData?.source_connection?.service_code || '';
    const destService = jobData?.destination_connection?.service_code || '';
    const jobScope = jobData?.is_sync ? ['all', 'sync'] : ['all', 'migration'];

    const loadScripts = () => {
        // Parse current value into script objects
        const entries = splitScripts(value);
        const parsed = entries.map(parseScriptEntry);
        setScripts(parsed);
        // All collapsed by default
        setExpandedIndices(new Set());

        console.log('[ScriptBuilder] Job services — source:', sourceService, '| destination:', destService);

        // Fetch schemas (cached)
        if (schemasCache.current) {
            setSchemas(schemasCache.current);
            setSchemasLoaded(true);
            return;
        }

        ApiRequest('GET', '/dmt/scripts').then(res => {
            const data = res?.data || [];
            console.log('[ScriptBuilder] Fetched script schemas:', data);
            schemasCache.current = data;
            setSchemas(data);
            setSchemasLoaded(true);
        }).catch(() => {
            schemasCache.current = [];
            setSchemas([]);
            setSchemasLoaded(true);
        });
    };

    const getSchemaForScript = (name) => schemas.find(s => s.name === name);

    /**
     * Get input fields for a script schema.
     * Supports new format (fields array with scope) and legacy (input_schema object).
     */
    const getInputFields = (schema) => {
        if (!schema) return [];
        // New format: fields array
        if (schema.fields) {
            return schema.fields
                .filter(f => f.scope === 'input')
                .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
                .map(f => [f.key, fieldToSchemaDef(f)]);
        }
        // Legacy format: input_schema object
        if (schema.input_schema) return Object.entries(schema.input_schema);
        return [];
    };

    /**
     * Get settings fields for a script schema.
     * Supports new format (fields array with scope) and legacy (settings_schema object).
     */
    const getSettingsFields = (schema) => {
        if (!schema) return [];
        // New format: fields array
        if (schema.fields) {
            return schema.fields
                .filter(f => f.scope === 'setting')
                .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
                .map(f => [f.key, fieldToSchemaDef(f)]);
        }
        // Legacy format: settings_schema object
        if (schema.settings_schema) return Object.entries(schema.settings_schema);
        return [];
    };

    /**
     * Convert new-format field row to the schema definition shape used by renderField.
     */
    const fieldToSchemaDef = (field) => ({
        type: field.type,
        label: field.label,
        description: field.description || '',
        required: field.required || false,
        core: field.core || false,
        default: resolveDefault(field.default),
        services: field.services ? field.services.split(',').map(s => s.trim()).filter(Boolean) : null,
        serviceSide: field.service_side || 'any',
        options: field.options || null,
    });

    /**
     * Resolve dynamic defaults like {{isSync}}.
     */
    const resolveDefault = (val) => {
        if (val === '{{isSync}}') return !!jobData?.is_sync;
        if (val === 'true') return true;
        if (val === 'false') return false;
        if (val !== null && val !== undefined && val !== '' && !isNaN(val)) return Number(val);
        return val;
    };

    /**
     * Bucket fields for the 3-section layout.
     * Bucket 1: core boolean settings (toggle row at top)
     * Bucket 2: core non-boolean settings + all inputs (2-col grid)
     * Bucket 3: non-core boolean settings (collapsible "Advanced")
     */
    const bucketFields = (schema) => {
        const allFields = [...getSettingsFields(schema), ...getInputFields(schema)];
        const bucket1 = []; // core boolean settings
        const bucket2 = []; // core non-boolean settings + inputs
        const bucket3 = []; // non-core boolean settings

        for (const [key, def] of allFields) {
            if (!isFieldVisible(def)) continue;
            const isInput = getInputFields(schema).some(([k]) => k === key);

            if (isInput) {
                bucket2.push([key, def]);
            } else if (def.type === 'boolean' && def.core) {
                bucket1.push([key, def]);
            } else if (def.type !== 'boolean' && def.core) {
                bucket2.push([key, def]);
            } else if (def.type === 'boolean' && !def.core) {
                bucket3.push([key, def]);
            } else {
                bucket2.push([key, def]);
            }
        }

        return { bucket1, bucket2, bucket3 };
    };

    const toggleAdvanced = (index) => {
        setAdvancedOpen(prev => {
            const next = new Set(prev);
            if (next.has(index)) next.delete(index);
            else next.add(index);
            return next;
        });
    };

    // Filter schemas by job scope
    const availableSchemas = schemas.filter(s => jobScope.includes(s.scope));

    // Scripts not yet added
    const unusedSchemas = availableSchemas.filter(s => !scripts.some(sc => sc.name === s.name));

    const toggleExpanded = (index) => {
        setExpandedIndices(prev => {
            const next = new Set(prev);
            if (next.has(index)) next.delete(index);
            else next.add(index);
            return next;
        });
    };

    const addScript = (schemaName) => {
        if (schemaName === '__raw__') {
            setScripts(prev => [...prev, { name: '', input: {}, settings: {}, raw: '' }]);
            // Expand newly added script
            setExpandedIndices(prev => new Set([...prev, scripts.length]));
        } else {
            const schema = getSchemaForScript(schemaName);
            // Pre-populate defaults from both input and settings fields so the
            // serialized script string carries the schema defaults (e.g. input:{input:site/id}).
            const seedDefaults = (fields) => {
                const out = {};
                for (const [key, def] of fields) {
                    if (def.default !== undefined && def.default !== null && def.default !== '' && isFieldVisible(def)) {
                        out[key] = def.default;
                    }
                }
                return out;
            };
            const inputDefaults = seedDefaults(getInputFields(schema));
            const settingsDefaults = seedDefaults(getSettingsFields(schema));
            setScripts(prev => [...prev, { name: schemaName, input: inputDefaults, settings: settingsDefaults, raw: null }]);
            // Expand newly added script
            setExpandedIndices(prev => new Set([...prev, scripts.length]));
        }
    };

    const removeScript = (index) => {
        setScripts(prev => prev.filter((_, i) => i !== index));
        // Adjust expanded indices after removal
        setExpandedIndices(prev => {
            const next = new Set();
            for (const idx of prev) {
                if (idx < index) next.add(idx);
                else if (idx > index) next.add(idx - 1);
            }
            return next;
        });
    };

    const updateScriptInput = (index, key, val) => {
        setScripts(prev => prev.map((s, i) => i === index ? { ...s, input: { ...s.input, [key]: val } } : s));
    };

    const updateScriptSetting = (index, key, val) => {
        setScripts(prev => prev.map((s, i) => i === index ? { ...s, settings: { ...s.settings, [key]: val } } : s));
    };

    const updateRawScript = (index, val) => {
        setScripts(prev => prev.map((s, i) => i === index ? { ...s, raw: val } : s));
    };

    // ─── Drag & Drop ────────────────────────────────────────────────────────

    const handleDragStart = (index) => {
        dragItem.current = index;
    };

    const handleDragEnter = (index) => {
        dragOverItem.current = index;
    };

    const handleDragEnd = () => {
        if (dragItem.current === null || dragOverItem.current === null || dragItem.current === dragOverItem.current) {
            dragItem.current = null;
            dragOverItem.current = null;
            return;
        }

        const fromIdx = dragItem.current;
        const toIdx = dragOverItem.current;

        setScripts(prev => {
            const updated = [...prev];
            const [moved] = updated.splice(fromIdx, 1);
            updated.splice(toIdx, 0, moved);
            return updated;
        });

        // Adjust expanded indices after reorder
        setExpandedIndices(prev => {
            const next = new Set();
            for (const idx of prev) {
                if (idx === fromIdx) {
                    next.add(toIdx);
                } else if (fromIdx < toIdx) {
                    next.add(idx > fromIdx && idx <= toIdx ? idx - 1 : idx);
                } else {
                    next.add(idx >= toIdx && idx < fromIdx ? idx + 1 : idx);
                }
            }
            return next;
        });

        dragItem.current = null;
        dragOverItem.current = null;
    };

    /**
     * Check if a field should be visible based on its services array and service_side.
     * Fields with no services array (null/empty) are always visible.
     * service_side: 'source' → only match source, 'destination' → only match dest, 'any' → match either.
     */
    const isFieldVisible = (fieldDef) => {
        if (!fieldDef.services || !Array.isArray(fieldDef.services) || fieldDef.services.length === 0) return true;
        if (!sourceService && !destService) return true;
        const side = fieldDef.serviceSide || 'any';
        if (side === 'source') return sourceService && fieldDef.services.includes(sourceService);
        if (side === 'destination') return destService && fieldDef.services.includes(destService);
        return fieldDef.services.includes(sourceService) || fieldDef.services.includes(destService);
    };

    /**
     * Filter select options by services and inherit service_side from parent field.
     * Option services can be a comma-separated string or an array.
     */
    const filterSelectOptions = (options, parentServiceSide) => {
        if (!options) return [];
        const side = parentServiceSide || 'any';
        return options.filter(opt => {
            if (!opt.services) return true;
            const svcList = Array.isArray(opt.services) ? opt.services : opt.services.split(',').map(s => s.trim());
            if (svcList.length === 0) return true;
            if (!sourceService && !destService) return true;
            if (side === 'source') return sourceService && svcList.includes(sourceService);
            if (side === 'destination') return destService && svcList.includes(destService);
            return svcList.includes(sourceService) || svcList.includes(destService);
        });
    };

    const handleApply = () => {
        const serialized = scripts
            .map(serializeScript)
            .filter(s => s && s.trim())
            .join(',');
        onChange?.(serialized);
        ToastBuilder('info', "Don't forget to Submit to save", {}, 5000);
    };

    // ─── Field Renderers ────────────────────────────────────────────────────

    const renderField = (fieldKey, fieldDef, value, onChangeField, scriptIndex) => {
        if (!isFieldVisible(fieldDef)) return null;

        const fieldValue = value !== undefined ? value : (fieldDef.default ?? '');
        const fieldId = `script-${scriptIndex}-${fieldKey}`;

        switch (fieldDef.type) {
            case 'boolean':
                return (
                    <Col key={fieldKey} md={6} className='mb-2'>
                        <div className='form-check d-flex align-items-center' style={{ gap: 6 }}>
                            <input
                                className='form-check-input'
                                type='checkbox'
                                id={fieldId}
                                checked={fieldValue === true || fieldValue === 'true'}
                                disabled={readOnly}
                                onChange={(e) => onChangeField(fieldKey, e.target.checked)}
                                style={{ marginTop: 0 }}
                            />
                            <label className='form-check-label' htmlFor={fieldId} style={{ fontSize: 12, cursor: 'pointer', marginBottom: 0 }}>
                                {fieldDef.label}
                            </label>
                        </div>
                        {fieldDef.description && <small className='text-muted' style={{ fontSize: 10, marginLeft: 22 }}>{fieldDef.description}</small>}
                    </Col>
                );

            case 'select': {
                const filteredOptions = filterSelectOptions(fieldDef.options || [], fieldDef.serviceSide);
                const selectOptions = filteredOptions.map(o => ({ value: o.value, label: o.label }));
                const currentValue = selectOptions.find(o => o.value === String(fieldValue)) || null;
                return (
                    <Col key={fieldKey} md={6} className='mb-2'>
                        <label style={{ fontSize: 12, fontWeight: 500 }}>{fieldDef.label}</label>
                        <div className='react-select-sm'>
                            <Select
                                className='react-select-container'
                                classNamePrefix='react-select'
                                options={selectOptions}
                                value={currentValue}
                                onChange={(opt) => onChangeField(fieldKey, opt?.value || '')}
                                isDisabled={readOnly}
                                isClearable
                                styles={selectStyles}
                                menuPortalTarget={document.body}
                                menuPosition='fixed'
                                placeholder='Select...'
                            />
                        </div>
                        {fieldDef.description && <small className='text-muted' style={{ fontSize: 10 }}>{fieldDef.description}</small>}
                    </Col>
                );
            }

            case 'number':
                return (
                    <Col key={fieldKey} md={6} className='mb-2'>
                        <label style={{ fontSize: 12, fontWeight: 500 }}>{fieldDef.label}</label>
                        <Input
                            bsSize='sm'
                            type='text'
                            value={fieldValue}
                            placeholder={fieldDef.description || ''}
                            readOnly={readOnly}
                            style={{ height: 30, fontSize: 12 }}
                            onChange={(e) => {
                                const val = e.target.value.replace(/[^0-9]/g, '');
                                onChangeField(fieldKey, val ? Number(val) : '');
                            }}
                        />
                        {fieldDef.description && <small className='text-muted' style={{ fontSize: 10 }}>{fieldDef.description}</small>}
                    </Col>
                );

            case 'text':
            default:
                return (
                    <Col key={fieldKey} md={6} className='mb-2'>
                        <label style={{ fontSize: 12, fontWeight: 500 }}>{fieldDef.label}</label>
                        <Input
                            bsSize='sm'
                            type='text'
                            value={fieldValue}
                            placeholder={fieldDef.description || ''}
                            readOnly={readOnly}
                            style={{ height: 30, fontSize: 12 }}
                            onChange={(e) => onChangeField(fieldKey, e.target.value)}
                        />
                        {fieldDef.description && <small className='text-muted' style={{ fontSize: 10 }}>{fieldDef.description}</small>}
                    </Col>
                );
        }
    };

    // ─── Script Card Renderer ───────────────────────────────────────────────

    const renderScriptCard = (script, index) => {
        const schema = getSchemaForScript(script.name);
        const isExpanded = expandedIndices.has(index);
        const isRaw = script.raw !== null && script.raw !== undefined;

        // Determine label and style class
        const label = isRaw ? (script.raw || 'Custom Script') : (schema?.label || script.name);
        const cardClass = schema ? 'section-group-accent' : 'section-group-muted';
        const labelColor = isRaw ? '#888' : undefined;

        // Summary text for collapsed view
        const getSummary = () => {
            if (isRaw) return script.raw ? script.raw.substring(0, 50) + (script.raw.length > 50 ? '...' : '') : '(empty)';
            const inputCount = Object.keys(script.input || {}).filter(k => script.input[k] !== '' && script.input[k] !== null && script.input[k] !== undefined).length;
            const settingsCount = Object.keys(script.settings || {}).filter(k => script.settings[k] !== '' && script.settings[k] !== null && script.settings[k] !== undefined).length;
            const parts = [];
            if (inputCount > 0) parts.push(`${inputCount} input${inputCount > 1 ? 's' : ''}`);
            if (settingsCount > 0) parts.push(`${settingsCount} setting${settingsCount > 1 ? 's' : ''}`);
            return parts.length > 0 ? parts.join(', ') : 'no config';
        };

        return (
            <div
                key={index}
                className={cardClass}
                style={{ borderRadius: 6, padding: isExpanded ? '10px 14px' : '6px 14px', marginBottom: 6, cursor: readOnly ? 'default' : 'grab', opacity: 1 }}
                draggable={!readOnly}
                onDragStart={() => handleDragStart(index)}
                onDragEnter={() => handleDragEnter(index)}
                onDragEnd={handleDragEnd}
                onDragOver={(e) => e.preventDefault()}
            >
                {/* Header — always visible */}
                <div className='d-flex justify-content-between align-items-center' style={{ minHeight: 28 }}>
                    <div className='d-flex align-items-center' style={{ gap: 8, flex: 1, cursor: 'pointer' }} onClick={() => toggleExpanded(index)}>
                        {!readOnly && <span style={{ fontSize: 11, color: '#aaa', cursor: 'grab', userSelect: 'none' }} title='Drag to reorder'>⠿</span>}
                        <span style={{ fontSize: 10, color: '#999', transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.15s', display: 'inline-block' }}>▶</span>
                        <span style={{ fontWeight: 600, fontSize: 13, color: labelColor }}>{isRaw ? 'RAW' : label}</span>
                        {!isExpanded && <small className='text-muted' style={{ fontSize: 11, marginLeft: 8 }}>{getSummary()}</small>}
                    </div>
                    <div className='d-flex align-items-center' style={{ gap: 4 }}>
                        {!isExpanded && schema?.description && <small className='text-muted' style={{ fontSize: 10 }}>{schema.category}</small>}
                        {!readOnly && <Button size='sm' color='link' className='text-danger p-0' style={{ lineHeight: 1 }} onClick={(e) => { e.stopPropagation(); removeScript(index); }}>✕</Button>}
                    </div>
                </div>

                {/* Body — only when expanded */}
                {isExpanded && (
                    <div style={{ marginTop: 8 }}>
                        {/* Raw script */}
                        {isRaw && (
                            <Input
                                bsSize='sm'
                                type='text'
                                value={script.raw}
                                readOnly={readOnly}
                                placeholder='e.g., link_cwm_company_site[{input:{resourceId:251}}]'
                                onChange={(e) => updateRawScript(index, e.target.value)}
                                style={{ fontFamily: 'monospace', fontSize: 12 }}
                            />
                        )}

                        {/* Registered script without schema */}
                        {!isRaw && !schema && (
                            <p style={{ fontSize: 11, color: '#888', margin: 0 }}>No schema found — script will be output with current settings</p>
                        )}

                        {/* Registered script with schema */}
                        {!isRaw && schema && (() => {
                            const { bucket1, bucket2, bucket3 } = bucketFields(schema);
                            const hasAny = bucket1.length > 0 || bucket2.length > 0 || bucket3.length > 0;

                            // Determine onChange handler for each field
                            const getOnChange = (key) => {
                                const isInput = getInputFields(schema).some(([k]) => k === key);
                                return isInput
                                    ? (k, v) => updateScriptInput(index, k, v)
                                    : (k, v) => updateScriptSetting(index, k, v);
                            };
                            const getFieldValue = (key) => {
                                const isInput = getInputFields(schema).some(([k]) => k === key);
                                return isInput ? script.input[key] : script.settings[key];
                            };

                            return (
                                <>
                                    {schema.description && <p style={{ fontSize: 11, color: '#666', margin: '0 0 8px' }}>{schema.description}</p>}

                                    {/* Bucket 1: Core boolean toggles — horizontal row */}
                                    {bucket1.length > 0 && (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: bucket2.length > 0 || bucket3.length > 0 ? 10 : 0 }}>
                                            {bucket1.map(([key, def]) => {
                                                const val = getFieldValue(key);
                                                const checked = val === true || val === 'true';
                                                const fieldId = `script-${index}-${key}`;
                                                return (
                                                    <div key={key} className='form-check d-flex align-items-center' style={{ gap: 6, marginBottom: 0 }}>
                                                        <input
                                                            className='form-check-input'
                                                            type='checkbox'
                                                            id={fieldId}
                                                            checked={checked}
                                                            disabled={readOnly}
                                                            onChange={(e) => getOnChange(key)(key, e.target.checked)}
                                                            style={{ marginTop: 0, flexShrink: 0 }}
                                                        />
                                                        <label className='form-check-label' htmlFor={fieldId} style={{ fontSize: 12, cursor: 'pointer', marginBottom: 0, fontWeight: 500, whiteSpace: 'nowrap' }}>
                                                            {def.label}
                                                        </label>
                                                        {def.description && <span style={{ fontSize: 11, color: '#888', marginLeft: 4 }}>— {def.description}</span>}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}

                                    {/* Divider between bucket 1 and 2 */}
                                    {bucket1.length > 0 && bucket2.length > 0 && (
                                        <hr style={{ margin: '8px 0', borderColor: 'var(--bs-border-color, #dee2e6)', opacity: 0.5 }} />
                                    )}

                                    {/* Bucket 2: Core non-boolean settings + inputs — 2-col grid */}
                                    {bucket2.length > 0 && (
                                        <Row>
                                            {bucket2.map(([key, def]) =>
                                                renderField(key, def, getFieldValue(key), getOnChange(key), index)
                                            )}
                                        </Row>
                                    )}

                                    {/* Divider before bucket 3 */}
                                    {bucket3.length > 0 && (bucket1.length > 0 || bucket2.length > 0) && (
                                        <hr style={{ margin: '8px 0', borderColor: 'var(--bs-border-color, #dee2e6)', opacity: 0.5 }} />
                                    )}

                                    {/* Bucket 3: Advanced boolean toggles — collapsible */}
                                    {bucket3.length > 0 && (
                                        <div>
                                            <div
                                                className='d-flex align-items-center'
                                                style={{ cursor: 'pointer', gap: 6, marginBottom: advancedOpen.has(index) ? 8 : 0 }}
                                                onClick={() => toggleAdvanced(index)}
                                            >
                                                <span style={{ fontSize: 10, color: '#999', transform: advancedOpen.has(index) ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.15s', display: 'inline-block' }}>▶</span>
                                                <span style={{ fontSize: 12, fontWeight: 500, color: '#666' }}>Advanced</span>
                                                <span style={{ fontSize: 10, color: '#999', background: 'var(--bs-tertiary-bg, #f0f0f0)', borderRadius: 8, padding: '1px 6px' }}>{bucket3.length}</span>
                                            </div>
                                            {advancedOpen.has(index) && (
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, paddingLeft: 4 }}>
                                                    {bucket3.map(([key, def]) => {
                                                        const val = getFieldValue(key);
                                                        const checked = val === true || val === 'true';
                                                        const fieldId = `script-${index}-adv-${key}`;
                                                        return (
                                                            <div key={key} className='form-check d-flex align-items-center' style={{ gap: 6, marginBottom: 2 }}>
                                                                <input
                                                                    className='form-check-input'
                                                                    type='checkbox'
                                                                    id={fieldId}
                                                                    checked={checked}
                                                                    disabled={readOnly}
                                                                    onChange={(e) => getOnChange(key)(key, e.target.checked)}
                                                                    style={{ marginTop: 0, flexShrink: 0 }}
                                                                />
                                                                <label className='form-check-label' htmlFor={fieldId} style={{ fontSize: 12, cursor: 'pointer', marginBottom: 0, fontWeight: 500, whiteSpace: 'nowrap' }}>
                                                                    {def.label}
                                                                </label>
                                                                {def.description && <span style={{ fontSize: 11, color: '#888', marginLeft: 4 }}>— {def.description}</span>}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {!hasAny && (
                                        <p style={{ fontSize: 11, color: '#888', margin: 0 }}>No configuration needed for this service combination</p>
                                    )}
                                </>
                            );
                        })()}
                    </div>
                )}
            </div>
        );
    };

    // ─── Add Script Dropdown ────────────────────────────────────────────────

    const addOptions = [
        ...unusedSchemas.map(s => ({ value: s.name, label: s.label || s.name })),
        { value: '__raw__', label: 'Custom (raw script)' },
    ];

    // ─── Render ─────────────────────────────────────────────────────────────

    return (
        <ModalButtonBuilder
            title='Script Builder'
            type='button'
            className='btn-info btn-sm'
            buttonStyle={{ minWidth: 110 }}
            modalTitle='After Create Scripts'
            formId='script-builder-form'
            style={{ width: 700, maxWidth: 700 }}
            onSubmit={readOnly ? false : 'Apply'}
            onCancelLabel={readOnly ? 'Close' : 'Cancel'}
            onClick={loadScripts}
            body={
                <Form id='script-builder-form' className='input-sm' onSubmit={(e) => { e.preventDefault(); e.stopPropagation(); handleApply(); }}>
                    {/* Add button */}
                    {!readOnly && (
                        <div className='d-flex justify-content-between align-items-center' style={{ marginBottom: 10 }}>
                            <span style={{ fontSize: 13, fontWeight: 600 }}>Configured Scripts</span>
                            <div style={{ minWidth: 250 }}>
                                <div className='react-select-sm'>
                                    <Select
                                        className='react-select-container'
                                        classNamePrefix='react-select'
                                        options={addOptions}
                                        value={null}
                                        onChange={(opt) => { if (opt) addScript(opt.value); }}
                                        placeholder='+ Add Script...'
                                        isDisabled={readOnly || !schemasLoaded}
                                        styles={selectStyles}
                                        menuPortalTarget={document.body}
                                        menuPosition='fixed'
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Script cards */}
                    {scripts.length === 0 && (
                        <p style={{ fontSize: 12, color: '#888', textAlign: 'center', margin: '20px 0' }}>
                            No scripts configured. Use the dropdown above to add one.
                        </p>
                    )}

                    {scripts.map((script, i) => renderScriptCard(script, i))}

                    {!schemasLoaded && (
                        <p style={{ fontSize: 12, color: '#888', textAlign: 'center' }}>Loading script schemas...</p>
                    )}
                </Form>
            }
        />
    );
};

export default ScriptBuilder;
